// Labelled textarea. Mirrors `FormInput`'s API; plan 24.

import { forwardRef, useId, type ReactNode, type TextareaHTMLAttributes } from 'react';
import { cn } from '../utils/cn';
import { INPUT_BASE_CLASS, INPUT_DENSITY, type InputDensity } from './FormInput';

export interface FormTextAreaProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'id'> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  id?: string;
  density?: InputDensity;
  containerClassName?: string;
}

export const FormTextArea = forwardRef<HTMLTextAreaElement, FormTextAreaProps>(
  function FormTextArea(
    {
      label,
      hint,
      error,
      id,
      density = 'comfortable',
      className,
      containerClassName,
      rows = 4,
      ...rest
    },
    ref
  ) {
    const generatedId = useId();
    const inputId = id ?? generatedId;
    const describedById = hint || error ? `${inputId}-desc` : undefined;

    return (
      <div className={cn('space-y-1.5', containerClassName)}>
        {label && (
          <label
            htmlFor={inputId}
            className="text-xs font-bold uppercase text-stone-400 ml-1 tracking-wider block"
          >
            {label}
          </label>
        )}
        <textarea
          ref={ref}
          id={inputId}
          rows={rows}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedById}
          className={cn(
            INPUT_BASE_CLASS,
            INPUT_DENSITY[density],
            'resize-none',
            error ? 'border-red-200' : 'border-stone-100',
            className
          )}
          {...rest}
        />
        {(hint || error) && (
          <p
            id={describedById}
            className={cn('text-xs ml-1', error ? 'text-red-600' : 'text-stone-500')}
          >
            {error ?? hint}
          </p>
        )}
      </div>
    );
  }
);
